<template>
  <div class="container mt-4">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-0"><i class="bi bi-stars me-2 text-warning"></i>Inspiración</h2>
        <small class="text-muted">
          {{ tab === 'pensamientos'
            ? 'Todas las reflexiones, de más reciente a más antigua'
            : tab === 'hoy'
              ? 'Tres frases aleatorias al día'
              : 'Banco de frases inspiradoras' }}
        </small>
      </div>
      <button
        v-if="tab === 'hoy' && !ocultarAccionesHoy"
        class="btn btn-outline-warning btn-sm"
        @click="otrasFrases"
        :disabled="cargandoHoy"
      >
        <span v-if="cargandoHoy" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="bi bi-shuffle me-1"></i>
        Otras tres frases
      </button>
    </div>

    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tab === 'pensamientos' }"
          @click="tab = 'pensamientos'"
        >
          <i class="bi bi-lightbulb me-1"></i>Pensamientos
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tab === 'hoy' }"
          @click="tab = 'hoy'"
        >
          <i class="bi bi-sun me-1"></i>Hoy
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tab === 'gestionar' }"
          @click="tab = 'gestionar'; cargarListado()"
        >
          <i class="bi bi-journal-text me-1"></i>Gestionar frases
        </button>
      </li>
    </ul>

    <div v-if="tab === 'hoy'">
      <FrasesDelDia ref="frasesHoy" :mostrar-enlace="false" />
    </div>

    <div v-else-if="tab === 'pensamientos'">
      <PensamientosTab />
    </div>

    <div v-else>
      <div v-if="mensaje" class="alert alert-success alert-dismissible">
        {{ mensaje }}
        <button type="button" class="btn-close" @click="mensaje = ''"></button>
      </div>
      <div v-if="errorGestion" class="alert alert-danger alert-dismissible">
        {{ errorGestion }}
        <button type="button" class="btn-close" @click="errorGestion = ''"></button>
      </div>

      <div class="card shadow-sm mb-4 border-warning">
        <div class="card-header bg-warning text-dark">
          <h5 class="mb-0">
            <i class="bi bi-book me-2"></i>
            Añadir desde un libro
          </h5>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-3">
            Indica un libro y se generarán frases inspiradoras basadas en su mensaje.
            Revisa la vista previa y elige cuáles añadir al banco.
          </p>

          <div v-if="generacion && !generacion.llmConfigured" class="alert alert-warning small py-2">
            <i class="bi bi-exclamation-triangle me-1"></i>
            Falta <code>OPENAI_API_KEY</code> en el servidor. Sin ella no se puede generar desde un libro.
          </div>

          <form @submit.prevent="generarDesdeLibro" class="row g-3">
            <div class="col-md-5">
              <label class="form-label">Libro *</label>
              <input
                v-model="libroForm.libro"
                type="text"
                class="form-control"
                required
                placeholder="Ej: El poder del ahora"
                :disabled="generando || !puedeGenerar"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Autor</label>
              <input
                v-model="libroForm.autor"
                type="text"
                class="form-control"
                placeholder="Opcional"
                :disabled="generando || !puedeGenerar"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label">Cantidad</label>
              <input
                v-model.number="libroForm.cantidad"
                type="number"
                class="form-control"
                :min="cantidadMin"
                :max="cantidadMax"
                :disabled="generando || !puedeGenerar"
              />
            </div>
            <div class="col-12 d-flex flex-wrap gap-2">
              <button
                type="submit"
                class="btn btn-warning"
                :disabled="generando || !puedeGenerar || !libroForm.libro.trim()"
              >
                <span v-if="generando" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="bi bi-magic me-1"></i>
                {{ generando ? 'Generando…' : 'Generar frases' }}
              </button>
              <button
                v-if="previewFrases.length"
                type="button"
                class="btn btn-outline-secondary"
                :disabled="generando || guardandoLote"
                @click="limpiarPreview"
              >
                Descartar vista previa
              </button>
            </div>
          </form>

          <div v-if="previewFrases.length" class="mt-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h6 class="mb-0">
                Vista previa
                <span class="badge bg-secondary ms-1">{{ seleccionadasCount }}/{{ previewFrases.length }}</span>
              </h6>
              <div class="btn-group btn-group-sm">
                <button type="button" class="btn btn-outline-secondary" @click="marcarTodas(true)">
                  Todas
                </button>
                <button type="button" class="btn btn-outline-secondary" @click="marcarTodas(false)">
                  Ninguna
                </button>
              </div>
            </div>
            <div class="list-group mb-3">
              <label
                v-for="(f, idx) in previewFrases"
                :key="idx"
                class="list-group-item list-group-item-action d-flex gap-3 align-items-start"
              >
                <input
                  v-model="f.seleccionada"
                  class="form-check-input mt-1 flex-shrink-0"
                  type="checkbox"
                />
                <span>
                  <span class="d-block">"{{ f.texto }}"</span>
                  <small class="text-muted">
                    {{ f.autor || 'Anónimo' }}
                    <span v-if="f.fuente"> — {{ f.fuente }}</span>
                  </small>
                </span>
              </label>
            </div>
            <button
              type="button"
              class="btn btn-success"
              :disabled="guardandoLote || seleccionadasCount === 0"
              @click="añadirSeleccionadas"
            >
              <span v-if="guardandoLote" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-plus-circle me-1"></i>
              Añadir {{ seleccionadasCount }} al banco
            </button>
          </div>
        </div>
      </div>

      <div class="card shadow-sm mb-4">
        <div class="card-header bg-light">
          <h5 class="mb-0">
            <i class="bi bi-plus-circle me-2"></i>
            {{ editando ? 'Editar frase' : 'Nueva frase' }}
          </h5>
        </div>
        <div class="card-body">
          <form @submit.prevent="guardarFrase" class="row g-3">
            <div class="col-12">
              <label class="form-label">Frase *</label>
              <textarea
                v-model="form.texto"
                class="form-control"
                rows="3"
                required
                placeholder="Escribe la cita o frase inspiradora"
              ></textarea>
            </div>
            <div class="col-md-6">
              <label class="form-label">Autor</label>
              <input v-model="form.autor" type="text" class="form-control" placeholder="Nombre del autor" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Fuente (libro, obra…)</label>
              <input
                v-model="form.fuente"
                type="text"
                class="form-control"
                placeholder="Ej: El poder del ahora"
              />
            </div>
            <div class="col-12 d-flex gap-2">
              <button type="submit" class="btn btn-warning" :disabled="guardando">
                <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                {{ editando ? 'Guardar cambios' : 'Añadir frase' }}
              </button>
              <button v-if="editando" type="button" class="btn btn-outline-secondary" @click="cancelarEdicion">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-if="cargandoListado" class="text-center py-4">
        <div class="spinner-border text-warning"></div>
      </div>

      <div v-else class="card shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span>
            <i class="bi bi-collection me-2"></i>
            Banco de frases
            <span v-if="config" class="badge bg-secondary ms-2">{{ config.total_activas }} activas</span>
          </span>
        </div>
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Frase</th>
                <th>Autor</th>
                <th>Fuente</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in frasesActivas" :key="f.id" :class="{ 'table-secondary': !f.activa }">
                <td class="frase-tabla-texto">"{{ truncar(f.texto, 80) }}"</td>
                <td>{{ f.autor || '—' }}</td>
                <td>{{ f.fuente || '—' }}</td>
                <td class="text-end text-nowrap">
                  <button class="btn btn-sm btn-outline-primary me-1" @click="editarFrase(f)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    v-if="f.activa"
                    class="btn btn-sm btn-outline-danger"
                    @click="eliminarFrase(f)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!frasesActivas.length">
                <td colspan="4" class="text-muted text-center py-4">No hay frases todavía</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from '../config/axios';
import FrasesDelDia from '../components/FrasesDelDia.vue';
import PensamientosTab from '../components/PensamientosTab.vue';

export default {
  name: 'Inspiracion',
  components: { FrasesDelDia, PensamientosTab },
  setup() {
    const route = useRoute();
    const tab = ref(
      route.query.compartir === 'fotos' || route.query.tab === 'pensamientos'
        ? 'pensamientos'
        : route.query.tab === 'hoy'
          ? 'hoy'
          : route.query.tab === 'gestionar'
            ? 'gestionar'
            : 'pensamientos'
    );
    const frasesHoy = ref(null);
    const cargandoHoy = ref(false);
    const listado = ref([]);
    const config = ref(null);
    const generacion = ref(null);
    const cargandoListado = ref(false);
    const guardando = ref(false);
    const mensaje = ref('');
    const errorGestion = ref('');
    const editando = ref(null);
    const form = ref({ texto: '', autor: '', fuente: '' });

    const libroForm = ref({ libro: '', autor: '', cantidad: 12 });
    const generando = ref(false);
    const guardandoLote = ref(false);
    const previewFrases = ref([]);

    const ocultarAccionesHoy = computed(() => !!frasesHoy.value?.todasLeidas);
    const frasesActivas = computed(() => listado.value.filter((f) => f.activa));
    const cantidadMin = computed(() => generacion.value?.cantidadMin ?? 3);
    const cantidadMax = computed(() => generacion.value?.cantidadMax ?? 20);
    const puedeGenerar = computed(() => generacion.value?.llmConfigured !== false);
    const seleccionadasCount = computed(
      () => previewFrases.value.filter((f) => f.seleccionada).length
    );

    function truncar(texto, max) {
      if (!texto || texto.length <= max) return texto;
      return texto.slice(0, max) + '…';
    }

    async function otrasFrases() {
      cargandoHoy.value = true;
      try {
        await frasesHoy.value?.refrescar();
      } finally {
        cargandoHoy.value = false;
      }
    }

    async function cargarListado() {
      cargandoListado.value = true;
      errorGestion.value = '';
      try {
        const { data } = await axios.get('/api/frases');
        listado.value = data.frases;
        config.value = data.config;
        generacion.value = data.generacion || null;
        if (data.generacion?.cantidadDefault && !previewFrases.value.length) {
          libroForm.value.cantidad = data.generacion.cantidadDefault;
        }
      } catch (e) {
        errorGestion.value = e.response?.data?.error || 'Error al cargar el listado';
      } finally {
        cargandoListado.value = false;
      }
    }

    function resetForm() {
      form.value = { texto: '', autor: '', fuente: '' };
      editando.value = null;
    }

    function editarFrase(f) {
      editando.value = f.id;
      form.value = { texto: f.texto, autor: f.autor || '', fuente: f.fuente || '' };
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function cancelarEdicion() {
      resetForm();
    }

    async function guardarFrase() {
      guardando.value = true;
      errorGestion.value = '';
      mensaje.value = '';
      try {
        if (editando.value) {
          await axios.put(`/api/frases/${editando.value}`, form.value);
          mensaje.value = 'Frase actualizada';
        } else {
          await axios.post('/api/frases', form.value);
          mensaje.value = 'Frase añadida al banco';
        }
        resetForm();
        await cargarListado();
        await frasesHoy.value?.cargar();
      } catch (e) {
        errorGestion.value = e.response?.data?.error || 'Error al guardar';
      } finally {
        guardando.value = false;
      }
    }

    async function eliminarFrase(f) {
      if (!confirm('¿Desactivar esta frase? Dejará de aparecer en la rotación.')) return;
      errorGestion.value = '';
      try {
        await axios.delete(`/api/frases/${f.id}`);
        mensaje.value = 'Frase desactivada';
        await cargarListado();
        await frasesHoy.value?.cargar();
      } catch (e) {
        errorGestion.value = e.response?.data?.error || 'Error al eliminar';
      }
    }

    function limpiarPreview() {
      previewFrases.value = [];
    }

    function marcarTodas(valor) {
      previewFrases.value.forEach((f) => {
        f.seleccionada = valor;
      });
    }

    async function generarDesdeLibro() {
      generando.value = true;
      errorGestion.value = '';
      mensaje.value = '';
      previewFrases.value = [];
      try {
        const { data } = await axios.post('/api/frases/generar-desde-libro', {
          libro: libroForm.value.libro.trim(),
          autor: libroForm.value.autor.trim() || undefined,
          cantidad: libroForm.value.cantidad,
        });
        previewFrases.value = (data.frases || []).map((f) => ({
          ...f,
          seleccionada: true,
        }));
        if (data.autor && !libroForm.value.autor.trim()) {
          libroForm.value.autor = data.autor;
        }
        mensaje.value = `Se generaron ${previewFrases.value.length} frases. Revísalas y añádelas al banco.`;
      } catch (e) {
        errorGestion.value = e.response?.data?.error || 'Error al generar frases desde el libro';
      } finally {
        generando.value = false;
      }
    }

    async function añadirSeleccionadas() {
      const frases = previewFrases.value
        .filter((f) => f.seleccionada)
        .map(({ texto, autor, fuente }) => ({ texto, autor, fuente }));
      if (!frases.length) return;

      guardandoLote.value = true;
      errorGestion.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.post('/api/frases/lote', { frases });
        const omitMsg = data.omitidas ? ` (${data.omitidas} ya existían)` : '';
        mensaje.value = `Se añadieron ${data.total} frases al banco${omitMsg}.`;
        limpiarPreview();
        libroForm.value = {
          libro: '',
          autor: '',
          cantidad: generacion.value?.cantidadDefault || 12,
        };
        await cargarListado();
        await frasesHoy.value?.cargar();
      } catch (e) {
        errorGestion.value = e.response?.data?.error || 'Error al añadir el lote';
      } finally {
        guardandoLote.value = false;
      }
    }

    onMounted(() => {
      if (route.query.tab === 'gestionar') {
        tab.value = 'gestionar';
        cargarListado();
      }
    });

    return {
      tab,
      frasesHoy,
      cargandoHoy,
      ocultarAccionesHoy,
      listado,
      config,
      generacion,
      cargandoListado,
      guardando,
      mensaje,
      errorGestion,
      editando,
      form,
      frasesActivas,
      libroForm,
      generando,
      guardandoLote,
      previewFrases,
      cantidadMin,
      cantidadMax,
      puedeGenerar,
      seleccionadasCount,
      truncar,
      otrasFrases,
      cargarListado,
      guardarFrase,
      editarFrase,
      cancelarEdicion,
      eliminarFrase,
      generarDesdeLibro,
      añadirSeleccionadas,
      limpiarPreview,
      marcarTodas,
    };
  },
};
</script>

<style scoped>
.frase-tabla-texto {
  max-width: 320px;
}
</style>
